<template>
  <div id="channels">
    <div>
      <p v-if="!products?.length" class="text-muted">{{ $t('orders.details.channels.empty') }}</p>
      <div class="row g-3" v-else>
        <div class="col-12 col-sm-6 col-lg-4 col-xl-3" v-for="product in products" :key="product.id">
          <div class="card border-2 h-100">
            <div class="card-body d-flex flex-column justify-content-between" style="padding: 15px">
              <div class="d-flex gap-3 justify-content-between align-items-start">
                <div class="flex-grow-1">
                  <h6 class="card-title mb-1 fw-bold">{{ product.name }}</h6>
                  <p class="card-text text-muted small mb-2" v-if="product.description">{{ product.description }}</p>
                </div>
                <div
                  class="avatar flex-shrink-0"
                  :style="{
                    backgroundImage: product.image?.data?.proxy?.url_thumbnail
                      ? `url('${product.image.data.proxy.url_thumbnail}')`
                      : 'none',
                    backgroundSize: 'contain',
                    width: '50px',
                    height: '50px',
                    borderRadius: '8px',
                    backgroundColor: product.image?.data ? 'transparent' : '#f8f9fa',
                  }">
                  <div
                    v-if="!product.image?.data"
                    class="d-flex align-items-center justify-content-center h-100 text-muted">
                    <i class="ti ti-package" style="font-size: 20px"></i>
                  </div>
                </div>
              </div>
              <div class="mt-2">
                <div class="row g-2">
                  <div class="col-6" v-if="product.budget_final">
                    <p class="text-muted small fw-medium mb-1">Budget</p>
                    <p class="text-muted small mb-0">
                      {{ formatCurrency(product.budget_final, product.currency_final || undefined) }}
                    </p>
                  </div>
                  <div class="col-6" v-if="product.duration">
                    <p class="text-muted small fw-medium mb-1">Duration</p>
                    <p class="text-muted small mb-0">{{ product.duration }} days</p>
                  </div>
                  <div class="col-6" v-if="product.start_final">
                    <p class="text-muted small fw-medium mb-1">Start</p>
                    <p class="text-muted small mb-0">{{ formatDate(product.start_final) }}</p>
                  </div>
                  <div class="col-6" v-if="product.end_final">
                    <p class="text-muted small fw-medium mb-1">End</p>
                    <p class="text-muted small mb-0">{{ formatDate(product.end_final) }}</p>
                  </div>
                </div>
                <div v-if="product.product_options && product.product_options.length" class="mt-2">
                  <p class="text-muted small fw-medium mb-1">Options</p>
                  <div class="d-flex flex-wrap gap-1">
                    <span
                      class="badge bg-light text-dark small"
                      v-for="option in product.option_names || []"
                      :key="option">
                      {{ option }}
                    </span>
                  </div>
                </div>
                <div id="product-badges" class="d-flex justify-content-end align-items-center mt-1">
                  <ContingentProductRefillBadge :product="product" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { formatCurrency } from '@/locale/format';
import type { IProductAllocation, IProductOption } from '@/types/product';
import { fetchOrderProducts } from '@/api/order';
import ContingentProductRefillBadge from '@/components/orders/product/ContingentProductRefillBadge.vue';

const products = ref<IProductAllocation[]>();

const props = defineProps<{
  orderId: string;
}>();

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

onMounted(() => {
  fetchOrderProducts(props.orderId, { resolve: 'image, job_product_options' }).then((res) => {
    const oProducts = res.data;
    oProducts.map((product) => {
      if (product.job_product_options) {
        product.option_names = getProductOptionsNames(product.job_product_options.data, product.product_options);
      }
    });
    products.value = oProducts;
  });
});

function getProductOptionsNames(
  jobProductOptions: IProductOption[],
  selectedOptions: { id: number; value: string }[] | null
) {
  const names: string[] = [];
  selectedOptions?.forEach((so) => {
    const foundOptionBlock = jobProductOptions.find((jpo) => jpo.id === so.id);
    if (foundOptionBlock) {
      const findTitle = foundOptionBlock.field_config.cards.find((card) => card.value === so.value)?.title || 'Unknown';
      names.push(findTitle);
    }
  });
  return names;
}
</script>

<style scoped>
.card {
  transition: border-color 0.2s ease-in-out;
}

.card:hover {
  border-color: var(--tblr-primary);
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
}

.card-text {
  font-size: 0.85rem;
  line-height: 1.4;
}
</style>
